/**
 * Application Registry Service
 * Manages BaseApplication instances for entities
 */

import * as Y from 'yjs';
import { BaseApplication } from './baseApplication/BaseApplication';
import { detectEntityVersion } from './entityVersionDetector';
import { DocsApplication } from '../apps/DocsApp_v2/services/DocsApplication';
import { CanvasApplication } from '../apps/CanvasApp_v2/services/CanvasApplication';
import { SheetsApplication } from '../apps/SheetsApp_v2/services/SheetsApplication';
import { PresentationsApplication } from '../apps/PresentationsApp_v2/services/PresentationsApplication';
import { NotesApplication } from '../apps/NotesApp_v2/services/NotesApplication';
import { PageApplication } from '../apps/PageApp_v2/services/PageApplication';
import { PlaybookApplication } from '../apps/PlaybookApp/PlaybookApplication';
import { AppBuilderApplication } from '../apps/AppBuilderApp/services/AppBuilderApplication';
import { WritersDashboardApplication } from '../apps/WritersDashboardApp_v2/services/WritersDashboardApplication';
import { BookFormatterApplication } from '../apps/BookFormatterApp_v2/services/BookFormatterApplication';
import { ManuscriptReviewReportApplication } from '../apps/ManuscriptReviewReportApp_v2/services/ManuscriptReviewReportApplication';
import { entityService } from './entityService';

/**
 * Application instance cache
 * entityId -> BaseApplication instance
 */
const applicationCache = new Map<string, BaseApplication>();

/**
 * In-flight creation promises keyed by resolved MongoDB entity ID.
 * Prevents concurrent calls from creating duplicate instances.
 */
const inFlightCreation = new Map<string, Promise<BaseApplication | null>>();

/**
 * Get or create BaseApplication instance for entity
 */
export async function getApplicationForEntity(entityIdOrLlmId: string): Promise<BaseApplication | null> {
  console.log('[ApplicationRegistry] Getting app for entity:', entityIdOrLlmId);
  
  // ✅ CRITICAL FIX: Resolve LLM ID to MongoDB ID BEFORE checking cache!
  // entity_write passes LLM IDs, but cache is keyed by MongoDB IDs
  const entity = await fetchEntity(entityIdOrLlmId);
  
  if (!entity) {
    console.log('[ApplicationRegistry] ❌ Entity not found');
    return null;
  }
  
  // Use the ACTUAL entity ID from MongoDB (not the input which might be LLM ID)
  const entityId = entity._id;
  
  console.log('[ApplicationRegistry] Resolved ID:', {
    input: entityIdOrLlmId,
    resolved: entityId,
    isLlmId: entityIdOrLlmId !== entityId
  });
  
  // Check cache with RESOLVED ID first, then fall back to the original input.
  // UI components call registerApplication() with whatever entityId prop they receive
  // (which may be an LLM ID), so we must check both keys.
  if (applicationCache.has(entityId)) {
    const cached = applicationCache.get(entityId)!;
    console.log('[ApplicationRegistry] ✅ Cache hit (resolved ID):', {
      entityId,
      instanceType: (cached as any).collaborationProvider ? 'UI_INSTANCE' : 'OFFLINE_INSTANCE',
    });
    return cached;
  }
  if (entityIdOrLlmId !== entityId && applicationCache.has(entityIdOrLlmId)) {
    const cached = applicationCache.get(entityIdOrLlmId)!;
    console.log('[ApplicationRegistry] ✅ Cache hit (LLM ID):', {
      entityId: entityIdOrLlmId,
      instanceType: (cached as any).collaborationProvider ? 'UI_INSTANCE' : 'OFFLINE_INSTANCE',
    });
    return cached;
  }

  // Guard against concurrent callers creating duplicate instances for the same entity.
  if (inFlightCreation.has(entityId)) {
    console.log('[ApplicationRegistry] ⏳ Waiting for in-flight creation:', entityId);
    return inFlightCreation.get(entityId)!;
  }
  
  const creationPromise = (async (): Promise<BaseApplication | null> => { try {
    // Check if v2
    const version = detectEntityVersion(entity);
    
    if (version !== 'v2') {
      console.log('[ApplicationRegistry] ⚠️ Entity is v1, cannot use universal commands');
      return null;
    }
    
    // Get appropriate application class
    const AppClass = getApplicationClassForEntity(entity);
    
    if (!AppClass) {
      console.log('[ApplicationRegistry] ❌ No app class for entity type:', entity.entityType);
      return null;
    }
    
    // Create application instance
    // @ts-ignore - AppClass is abstract but we know the concrete type
    const app = new AppClass() as BaseApplication;
    
    // Load entity data (including Y.js state)
    // All v2 apps have a load() method, but it's not defined in BaseApplication
    // We need to check if the method exists and call it
    if (typeof (app as any).load === 'function') {
      console.log('[ApplicationRegistry] 🔧 Creating offline instance for:', entityId);
      await (app as any).load(entityId, { offline: true });
      console.log('[ApplicationRegistry] ✅ Offline instance loaded for:', entityId);
    } else {
      // Fallback to initialize for apps that don't have load()
      await app.initialize(entityId);
    }
    
    // Cache it
    applicationCache.set(entityId, app);
    
    console.log('[ApplicationRegistry] ✅ Created and cached app for:', entityId);
    
    return app;
    
  } catch (error: any) {
    console.error('[ApplicationRegistry] Error:', error);
    return null;
  } finally {
    inFlightCreation.delete(entityId);
  }
  })();

  inFlightCreation.set(entityId, creationPromise);
  return creationPromise;
}

/**
 * Fetch entity from backend
 */
async function fetchEntity(entityId: string): Promise<any> {
  console.log('[ApplicationRegistry] Fetching entity:', entityId);
  
  // Use actual entityService
  return await entityService.get(entityId);
}

/**
 * Get application class for entity
 */
function getApplicationClassForEntity(entity: any): typeof BaseApplication | null {
  const type = entity.entityType;
  
  console.log('[ApplicationRegistry] Getting app class for type:', type);
  
  // Map entity types to application classes
  switch (type) {
    case 'Doc':
    case 'Document':
      return DocsApplication as any;
    
    case 'Canvas':
      return CanvasApplication as any;
    
    case 'Sheet':
    case 'Spreadsheet':
      return SheetsApplication as any;
    
    case 'Presentation':
    case 'Slides':
      return PresentationsApplication as any;
    
    case 'Note':
    case 'Content.Note':
      return NotesApplication as any;

    case 'Page':
    case 'RiverBot':
      return PageApplication as any;
    
    case 'Playbook':
      return PlaybookApplication as any;
    
    case 'app_project':
    case 'AppProject':
    case 'App':
      return AppBuilderApplication as any;

    case 'WritersDashboard':
      return WritersDashboardApplication as any;
    
    case 'BookProject':
    case 'Project.BookProject':
      return BookFormatterApplication as any;

    case 'ManuscriptReviewReport':
      return ManuscriptReviewReportApplication as any;
    
    default:
      // File entities (File.Code.*, File.Document.*, File.Data.*) are handled by FileEditorApp
      // and do not use BaseApplication. Agents access file content via entity.skeleton.content
      // which FileEditorApplication keeps in sync on every auto-save.
      if (type?.startsWith('File.')) {
        console.log('[ApplicationRegistry] File entity — content accessible via skeleton.content:', type);
        return null;
      }
      console.warn('[ApplicationRegistry] Unsupported entity type:', type);
      return null;
  }
}

/**
 * Clear application from cache
 * Call when entity is closed
 */
export function clearApplicationCache(entityId: string): void {
  const app = applicationCache.get(entityId);
  
  if (app) {
    console.log('[ApplicationRegistry] Destroying app for:', entityId);
    app.destroy();
    applicationCache.delete(entityId);
  }
}

/**
 * Synchronously look up an existing application instance in the cache.
 * Returns null if nothing is registered — never creates a new instance.
 * Used by UI components to check whether an offline instance already exists
 * before deciding whether to create their own or adopt the existing one.
 */
export function getApplicationFromCache(entityId: string): BaseApplication | null {
  const result = applicationCache.get(entityId) || null;
  console.log('[ApplicationRegistry] getApplicationFromCache:', entityId,
    result ? `FOUND (${(result as any).collaborationProvider ? 'online' : 'offline'})` : 'NOT_FOUND');
  return result;
}

/**
 * Register an existing application instance
 * ✅ CRITICAL: Used by UI components that create their own instances
 * This ensures entity_write reuses the same instance instead of creating a new one!
 */
export function registerApplication(entityId: string, app: BaseApplication): void {
  console.log('[ApplicationRegistry] Registering existing app instance:', entityId);

  // Remove any stale listener from a previous registration for this entityId.
  const previousApp = applicationCache.get(entityId);
  if (previousApp && (previousApp as any).__offlineSaveListener) {
    window.removeEventListener('entity-offline-saved', (previousApp as any).__offlineSaveListener);
    delete (previousApp as any).__offlineSaveListener;
  }
  // Also remove from the incoming app itself if it was previously registered
  // (handles re-registration of the same instance).
  if ((app as any).__offlineSaveListener) {
    window.removeEventListener('entity-offline-saved', (app as any).__offlineSaveListener);
    delete (app as any).__offlineSaveListener;
  }

  applicationCache.set(entityId, app);

  // When an offline instance writes to this entity while this UI instance is already
  // mounted, merge the saved state into our Y.Doc so elements reload immediately.
  const handler = (event: Event) => {
    const { entityId: savedId, update } = (event as CustomEvent<{ entityId: string; update: Uint8Array }>).detail;
    if (savedId !== entityId) return;
    const ydoc = (app as any).ydoc as Y.Doc | undefined;
    if (!ydoc) return;
    console.log('[ApplicationRegistry] 🔀 Merging offline save into UI instance:', entityId);
    Y.applyUpdate(ydoc, update, 'offline-save-sync');
  };

  window.addEventListener('entity-offline-saved', handler);
  (app as any).__offlineSaveListener = handler;
}

/**
 * Unregister an application instance WITHOUT destroying it
 * ✅ FIX: Used when component manages its own lifecycle
 * Component will call destroy() separately
 */
export function unregisterApplication(entityId: string): void {
  console.log('[ApplicationRegistry] Unregistering app instance:', entityId);
  const app = applicationCache.get(entityId);
  if (app && (app as any).__offlineSaveListener) {
    window.removeEventListener('entity-offline-saved', (app as any).__offlineSaveListener);
    delete (app as any).__offlineSaveListener;
  }
  applicationCache.delete(entityId);
}

/**
 * Synchronously get a cached application instance by entity ID.
 * Returns null if the application hasn't been loaded yet.
 */
export function getApplication(entityId: string): BaseApplication | null {
  return applicationCache.get(entityId) ?? null;
}

/**
 * Clear all cached applications
 */
export function clearAllApplications(): void {
  console.log('[ApplicationRegistry] Clearing all applications');
  for (const [entityId, app] of applicationCache.entries()) {
    if ((app as any).__offlineSaveListener) {
      window.removeEventListener('entity-offline-saved', (app as any).__offlineSaveListener);
      delete (app as any).__offlineSaveListener;
    }
    app.destroy();
  }
  applicationCache.clear();
  inFlightCreation.clear();
}

